import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Grid,
  MenuItem,
  FormControl,
  Select,
  OutlinedInput,
  CircularProgress,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import useConstants from "constants";
import PropTypes from "prop-types";

const PageSettings = ({
  currentSortColumn = "id",
  currentSortDirection = "desc",
  currentItemsPerPage = 10,
  sortableColumns = [],
  itemsPerPageOptions,
  defaultSortColumn = "id",
  defaultSortDirection = "desc",
  defaultItemsPerPage = 10,
  onSettingsChange,
  onResetSettings,
  tableId,
  saveBtnLoading,
}) => {
  const { t } = useTranslation();
  const { items_per_page_options } = useConstants();

  // Use provided options or fall back to constants
  const paginationOptions = itemsPerPageOptions || items_per_page_options;

  // Local state for settings
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  // Available sort directions
  const sortDirections = [
    { value: "asc", label: t("lang.ascending") || "Ascending" },
    { value: "desc", label: t("lang.descending") || "Descending" },
  ];

  const handleSave = () => {
    const settings = {
      sortColumn,
      sortDirection,
      itemsPerPage: parseInt(itemsPerPage),
    };

    const tableSettings = {
      [tableId]: settings,
    };

    if (onSettingsChange) {
      onSettingsChange(tableSettings);
    }
  };

  const handleReset = () => {
    setSortColumn(defaultSortColumn);
    setSortDirection(defaultSortDirection);
    setItemsPerPage(defaultItemsPerPage);
    if (onResetSettings) {
      onResetSettings();
    }
  };

  return (
    <SoftBox p={3}>
      {/* Default Sorting Settings */}
      <Card sx={{ mb: 3, borderRadius: "10px" }}>
        <SoftBox p={3}>
          <SoftTypography variant="h6" fontWeight="medium" mb={2}>
            {t("lang.default_sorting") || "Default Sorting"}
          </SoftTypography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SoftBox mb={1}>
                <FormControl fullWidth>
                  <SoftTypography variant="overline" gutterBottom>
                    {t("lang.sort_column") || "Sort Column"}
                  </SoftTypography>
                  <Select
                    value={sortColumn}
                    onChange={(e) => setSortColumn(e.target.value)}
                    fullWidth
                    input={<OutlinedInput sx={{ height: 56 }} />}
                    sx={{ padding: "10px" }}
                  >
                    {sortableColumns.map((column) => (
                      <MenuItem key={column.value} value={column.value}>
                        {column.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </SoftBox>
            </Grid>

            <Grid item xs={12} sm={6}>
              <SoftBox mb={1}>
                <FormControl fullWidth>
                  <SoftTypography variant="overline" gutterBottom>
                    {t("lang.sort_direction") || "Sort Direction"}
                  </SoftTypography>
                  <Select
                    value={sortDirection}
                    onChange={(e) => setSortDirection(e.target.value)}
                    fullWidth
                    input={<OutlinedInput sx={{ height: 56 }} />}
                    sx={{ padding: "10px" }}
                  >
                    {sortDirections.map((direction) => (
                      <MenuItem key={direction.value} value={direction.value}>
                        {direction.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </Card>

      {/* Items Per Page Settings */}
      <Card sx={{ mb: 3, borderRadius: "10px" }}>
        <SoftBox p={3}>
          <SoftTypography variant="h6" fontWeight="medium" mb={2}>
            {t("lang.pagination_settings") || "Pagination Settings"}
          </SoftTypography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SoftBox mb={1}>
                <FormControl fullWidth>
                  <SoftTypography variant="overline" gutterBottom>
                    {t("lang.items_per_page") || "Items Per Page"}
                  </SoftTypography>
                  <Select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(e.target.value)}
                    fullWidth
                    input={<OutlinedInput sx={{ height: 56 }} />}
                    sx={{ padding: "10px" }}
                  >
                    {paginationOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </Card>

      {/* Action Buttons */}
      <SoftBox display="flex" justifyContent="end" alignItems="center">
        {/* <SoftButton variant="outlined" color="secondary" onClick={handleReset}>
          {t("lang.reset") || "Reset"}
        </SoftButton> */}

        <SoftButton
          variant="gradient"
          color="info"
          onClick={handleSave}
          disabled={saveBtnLoading}
          startIcon={saveBtnLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {t("lang.actions.save") || "Save Settings"}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
};

PageSettings.propTypes = {
  currentSortColumn: PropTypes.string,
  currentSortDirection: PropTypes.string,
  currentItemsPerPage: PropTypes.number,
  sortableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  defaultSortColumn: PropTypes.string,
  defaultSortDirection: PropTypes.string,
  defaultItemsPerPage: PropTypes.number,
  onSettingsChange: PropTypes.func,
  onResetSettings: PropTypes.func,
  tableId: PropTypes.string,
  saveBtnLoading: PropTypes.bool,
};

export default PageSettings;
